<template>

	<Drawer :title="inUpdateMode? $t('Edit pupil') : $t('Add pupil')" initialWidth="lg" @requestUnmount="hide">

		<template #default="{ hide }">

			<div v-if="suggestMatchingPupils" class="alert alert-info text-sm">
				{{ $t('#instruction: begin to type to find matches in the pupil directory') }}
			</div>

			<form @submit.prevent="save" class="isolate">

				<div class="pb-16 px-6">

					<div class="bg-base pt-4">
						<p v-if="pupilsStore.errors?.generic" class="text-red-600 text-xs">
							{{ pupilsStore.errors?.generic.toLocaleString($i18n) }}
						</p>

						<div class="grid grid-cols-6 gap-6 row">
							<div class="col-span-6 sm:col-span-6 relative">
								<sms-input type="text"
									@focus="focusedInput = $event.target; focusedField = 'firstName'"
									@blur="focusedInput = null; autoCompleteSuggestions = []"
									autofocus
									v-model="editableModel.firstName"
									:label="$t('First name')"
									:validationError="pupilsStore.errors?.fields.firstName?.toLocaleString($i18n)"
									@input="(event) => refreshAutocomplete('firstName', event.target.value)"
									autocomplete="off" spellcheck="false"
								></sms-input>
							</div>

							<div class="col-span-6 sm:col-span-6 relative">
								<sms-input type="text"
									@focus="focusedInput = $event.target; focusedField = 'lastName'"
									@blur="focusedInput = null; autoCompleteSuggestions = []"
									v-model="editableModel.lastName"
									:label="$t('Last name')"
									:validationError="pupilsStore.errors?.fields.lastName?.toLocaleString($i18n)"
									@input="(event) => refreshAutocomplete('lastName', event.target.value)"
									autocomplete="off" spellcheck="false"
								></sms-input>
							</div>

							<div class="col-span-6 sm:col-span-4" ref="dobWrapper">
								<DateSelector
									v-model="dateOfBirth"
									:label="$t('Date of birth')"
									input-css-classes="input-bordered"
									:validationError="pupilsStore.errors?.fields.birthdate?.toLocaleString($i18n)"
									:start-date="datePickerStartDate"
									:year-range="[new Date().getFullYear() - 14, new Date().getFullYear()]"
									:max-date="new Date()"
									:flow="dateOfBirth !== null? ['calendar'] : ['year', 'month', 'calendar']"
									autocomplete="off" spellcheck="false"
									@focus="focusedField = 'dateOfBirth'"
									@update:model-value="(event) => refreshAutocomplete('dateOfBirth', event)"
								/>
							</div>
						</div>
					</div>

					<PupilFilePicker
						v-if="organisationsStore.getCurrentOrganisation().pupil_cloud_link_enabled ?? false"
						v-model="pupilFile"
						v-model:uploading="attachmentsBusy"
						:accepts="[ 'files', 'folders' ]"
						:readOnly="scope !== 'organisation'"
					/>

					<template v-if="showClassroomSelector">
						<div class="border-b border-base-200 mt-8">
							{{ $tc('Classroom', 2) }}
						</div>

						<div v-if="!classrooms.length" class="text-base-content-light py-4 italic text-center">
							{{ $t('No classrooms yet') }}
						</div>

						<div v-else class="flex flex-col mt-4">
						<div v-for="(classroom, index) in classrooms" class="flex justify-between w-full px-4 py-1 rounded">
							<label
								:for="'cr-' + classroom.id"
								class="hover:cursor-pointer"
							>
									<div class="flex gap-3 items-center py-2">
										<input
											type="checkbox"
											:id="'cr-' + classroom.id"
											class="checkbox checkbox-sm checkbox-primary"
											:checked="editableModel.getClassroomRelation(classroom)?.active"
											@change.prevent="event => editableModel.toggleClassroom(classroom, event.target.checked)"
										/>
										<span>{{ classroom.name }}</span>
									</div>
								</label>
								<div class="mx-2" v-if="editableModel.getClassroomRelation(classroom)?.active && editableModel.getClassroomRelation(classroom).objectIcon !== null">
									<PupilSymbol :pupil="editableModel" :classroom="classroom" class="w-10"/>
								</div>
							</div>
						</div>
					</template>

					<template v-else>
						<div class="input-label border-b border-base-200 mt-4">
							{{ $t('Symbol') }}
						</div>

						<div class="flex pt-4 items-center flex-col">
							<div>
								<div class="shrink-0 hover:cursor-pointer relative group transition-all duration-300" @click="showIconSelector = true">
									<div class="btn btn-circle bg-base-100 z-20 absolute -right-3 -top-2 group-hover:bg-base-200 group-hover:border-base-300 shadow-md">
										<PencilIcon class="w-5 h-5"></PencilIcon>
									</div>
									<PupilSymbol :pupil="editableModel" class="w-32 group-hover:scale-105 transition-all"/>
								</div>
							</div>
						<!-- <div>
							<button type="button" class="btn" @click="showIconSelector = true">
								{{ $t('Change icon' ) }}...
							</button>
						</div> -->

						<!-- Color selector -->
							<div class="grid grid-cols-4 sm:grid-cols-8 gap-3 mt-4">
								<div
									v-for="color in availableColors"
									@click="editableModel.setSymbolColorForClassroom(currentClassroom, color)"
									:key="color"
									class="cursor-pointer flex items-center justify-center"
									:class="'w-10 h-10 rounded-full bg-' + color + '-200 border-2 border-' + color + '-400 '"
								>
									<CheckIcon v-if="editableModel.getSymbolColorForClassroom(currentClassroom) === color" class="w-6 h-6" />
								</div>
							</div>
						</div>
					</template>

				</div>


				<div class="drawer-action-bar" :class="[inUpdateMode? 'justify-between' : 'justify-end']">

					<Dropdown v-if="inUpdateMode" position="top-start">
						<template #trigger>
							<button v-if="scope === 'organisation'" type="button" class="btn" :class="[saving? 'btn-disabled':'btn-danger btn-outline']">
								<span v-show="deleting" class="loading loading-spinner"></span>
								<TrashIcon class="w-5 h-5" />
							</button>
							<button v-else type="button" class="btn" :class="[saving? 'btn-disabled':'btn-danger btn-outline']">
								<span v-show="deleting" class="loading loading-spinner"></span>
								{{ $t('Remove from classroom')}}
							</button>
						</template>
						<template #content="dialogActions">
							<DropdownDialogConfirm :confirm-label="$t('Yes')" :cancel-label="$t('No')" confirm-style="danger" class="w-80" @cancel="dialogActions.close" @confirm="confirmRemove(dialogActions)">
								<span class="text-danger">{{ $t('Are you sure?') }}</span>
							</DropdownDialogConfirm>
						</template>
					</Dropdown>

					<div class="flex justify-end gap-1">
						<button type="button" class="btn btn-ghost" @click="hide" :class="[saving || deleting? 'hidden':'']">
							{{ $t('Cancel') }}
						</button>
						<button type="submit" class="btn" :class="[deleting || attachmentsBusy? 'btn-disabled':'btn-primary']">
							<span v-show="saving" class="loading loading-spinner"></span>
							{{ $t('Save') }}
						</button>
					</div>

				</div>

			</form>

			<div v-show="autoCompleteSuggestions.length > 0 && autoCompleteSourceField === focusedField"
				class="fixed z-50 bg-white border rounded p-1 mt-1 shadow-md ml-6"
				:style="dropdownStyle"
			>
				<div class="px-1 py-1 font-bold">{{ $t('Do you mean') }}...</div>
				<ul class="flex flex-col gap-1">
					<li v-for="suggestion in autoCompleteSuggestions" :key="suggestion.id" class="py-2 px-2 flex justify-between gap-2 bg-base-100 rounded hover:cursor-pointer hover:bg-accent" @mousedown.prevent="selectSuggestion(suggestion)">
						<span class="flex grow items-center gap-3">
							<input type="radio" class="radio radio-sm" />
							{{ suggestion.firstName }} {{ suggestion.lastName }}
						</span>
						<span v-show="suggestion.birthdate" class="px-2">*{{ suggestion.birthdate?.toFormat('dd/MM/yyyy') }}</span>
					</li>
				</ul>
			</div>
		</template>

	</Drawer>

	<IconSelector
		v-if="showIconSelector"
		:isIconSelectableCallback="isIconSelectable"
		:group="'pupil'"
		:model="editableModel.getClassroomRelation(currentClassroom)"
		:classroom="currentClassroom"
		@icon:selected="editableModel.setIconForClassroom(currentClassroom, $event)"
		@request-close="showIconSelector = false"
	/>

</template>


<script lang="ts">
import { mapStores } from 'pinia';

import { useIconsStore } from '@/stores/Icons.store'
import { usePupilsStore } from "@/stores/Pupils.store";
import {useClassroomsStore} from "@/stores/Classrooms.store";
import { usePreferencesStore } from "@/stores/Preferences.store";
import { useOrganisationsStore } from '@/stores/Organisations.store';
import { ApiErrorMessageBag } from "@/stores/errors/ApiErrorMessageBag.ts";

import { Pupil } from "@/models/Pupil.model";

import PupilSymbol from "@/components/ui/symbols/PupilSymbol.vue";
import Dropdown from "@/components/ui/Dropdown.vue";
import DropdownDialogConfirm from "@/components/ui/DropdownDialogConfirm.vue";
import { TrashIcon, CheckIcon, MagnifyingGlassIcon, CalendarIcon, PencilIcon } from '@heroicons/vue/24/outline';
import { Icon } from "@/models/Icon.model";
import Drawer from '@/components/ui/Drawer.v2.vue';
import DateSelector from "@/components/ui/DateSelector.vue";
import IconSelector from "@/components/IconSelector.vue";
import PupilFilePicker from "@/components/PupilFilePicker.vue";
import {DateTime} from "luxon";

export default {
	emits: ['requestClose'],

	props: {
		pupil: {
			type: Pupil,
			required: true,
		},
		scope: {
			type: String,
			default: 'organisation',
		},
	},

	components: {
		Dropdown,
		DropdownDialogConfirm,
		PupilSymbol,
		TrashIcon, CheckIcon, MagnifyingGlassIcon, CalendarIcon, PencilIcon,
		Drawer,
		DateSelector,
		IconSelector,
		PupilFilePicker,
	},

	data() {
		return {
			canSelectCustomIcons: false,
			saving: false,
			deleting: false,
			availableColors: Pupil.COLORS,
			selectedIconLibraryId: 'default',
			originalModel: null,
			editableModel: null,
			showIconSelector: null,
			autoCompleteTimeout: null,
			autoCompleteSuggestions: [],
			attachmentsBusy: false,
			dropdownStyle: { top: '0px', width: '0px' },
			focusedInput: null,
			autoCompleteSourceField: null,
		}
	},

	computed: {
		...mapStores(useIconsStore, usePupilsStore, usePreferencesStore, useClassroomsStore, useOrganisationsStore),

		inUpdateMode() {
			return (this.pupil && this.pupil.id)? true : false;
		},

		datePickerStartDate() {
			const currentYear = new Date().getFullYear();
			return new Date(currentYear - 3, 0, 1);
		},

		dateOfBirth: {
			get(): Date {
				return this.editableModel.birthdate;
			},
			set(value: Date) {
				if (!value) {
					this.editableModel.birthdate = null;
					return;
				}
				this.editableModel.birthdate = DateTime.fromJSDate(value);
			}
		},

		classrooms() {
			return this.classroomsStore.items;
		},

		currentClassroom() {
			return this.classroomsStore.currentClassroom;
		},

		showClassroomSelector() {
			return this.scope === 'organisation';
		},

		/**
		 * Bridges the single-file PupilFilePicker v-model to the pupil's files array.
		 * Returns the first file or null; setting replaces the entire array.
		 */
		pupilFile: {
			get() {
				return this.editableModel?.files?.[0] ?? null;
			},
			set(value: any) {
				if (this.editableModel) {
					this.editableModel.files = value ? [value] : [];
				}
			}
		},

		suggestMatchingPupils() {
			return !this.inUpdateMode && this.scope === 'classroom';
		},
	},

	beforeMount() {
		if(this.pupil && this.pupil.id) {
			this.originalModel = this.pupil;
			// clone the store model to allow for manipulation without instantly affecting the store
			this.editableModel = this.pupil.clone();

		} else {
			this.editableModel = this.pupil;
		}

		if (this.scope === 'organisation') {
			this.classroomsStore.load();
		}
	},

	watch: {
		focusedInput() {
			this.positionDropdown();
		}
	},

	methods: {
		hide () {
			this.pupilsStore.clearErrors();
			this.$emit('requestClose');
		},

		isIconSelectable(icon: Icon) {
			return !this.isIconUsedForOtherPupil(icon);
		},

		isIconUsedForOtherPupil(icon: Icon) {
			const pupilIcon = this.editableModel.getIconForClassroom(this.currentClassroom);
			if(pupilIcon && pupilIcon.equals(icon)) {	// used by this pupil
				return false;
			} else {
				return this.pupilsStore.isIconUsed(icon);
			}
		},

		positionDropdown() {
			if (!this.focusedInput) return;
			const rect = this.focusedInput.getBoundingClientRect();
			this.dropdownStyle = { top: `${rect.bottom}px`, width: `${rect.width}px` };
		},

		async confirmRemove(dialogActions) {
			dialogActions.close();
			if (this.deleting) {
				return;
			}
			this.deleting = true;

			if(this.scope === 'organisation') {
				try {
					await this.pupilsStore.delete(this.originalModel);
					this.deleting = false;

					this.hide();

					this.$notify({
						text: this.$t('The pupil has been removed'),
						type: "success",
					});

				} catch (e: any) {
					if (!(e instanceof ApiErrorMessageBag)) {
						throw e;
					}
				}

			} else {

				try {
					await this.editableModel.toggleClassroom(this.classroomsStore.currentClassroom, false);
					await this.pupilsStore.save(this.editableModel);

					this.deleting = false;
					this.hide();

					this.$notify({
						text: this.$t('The pupil has been removed'),
						type: "success",
					});
				} catch (e: any) {

					this.deleting = false;

					if (e instanceof ApiErrorMessageBag) {
						this.$notify({
							text: this.$t(e.toString()),
							type: "error",
						});
					} else {
						throw e;
					}
				}
			}
		},

		async save() {

			if (this.saving) {
				return;
			}
			this.saving = true;

			try {
				await this.pupilsStore.save(this.editableModel);

				this.saving = false;
				this.hide();

				this.$notify({
					text: this.$t('The pupil has been saved'),
					type: "success",
				});

			} catch (e) {
				// error message is populated now.
				this.saving = false;
			}
		},

		refreshAutocomplete(updatedField, newValue) {
			if (!this.suggestMatchingPupils) {
				return;
			}

			const anchorEl = updatedField === 'dateOfBirth'
				? this.$refs.dobWrapper
				: this.focusedInput;

			// initialize
			let fn = this.editableModel.firstName;
			let ln = this.editableModel.lastName;
			let dob = this.editableModel.birthdate;

			// editableModel value for the changed field may not be updated yet, so take the changed value straight from the event
			switch(updatedField) {
				case 'firstName':
					fn = newValue;
					break;
				case 'lastName':
					ln = newValue;
					break;
				case 'dateOfBirth':
					if(newValue instanceof Date) {
						dob = DateTime.fromJSDate(newValue);
					} else {
						dob = null;
					}
					break;
			}

			// only start searching if at least one field has a useful value
			if( (!fn || fn.length < 2) &&
				(!ln || ln.length < 2) &&
				(dob === null)
			) {
				this.autoCompleteSuggestions = [];
				return;
			}

			if (this.autoCompleteTimeout) {
				clearTimeout(this.autoCompleteTimeout);
			}

			this.autoCompleteTimeout = setTimeout(async () => {
				const results = (await this.pupilsStore.findByNameAndDateOfBirth(
					fn, ln, dob ? dob.toSQLDate() : dob
				)).filter((pupil: Pupil) => {
					return !pupil.belongsToClassroom(this.currentClassroom);
				}).slice(0, 6);

				if (this.focusedField === updatedField) {
					this.autoCompleteSuggestions = results;
					this.autoCompleteSourceField = updatedField;
					this.$nextTick(() => {
					if (!anchorEl) return;
					const rect = anchorEl.getBoundingClientRect();
					this.dropdownStyle = { top: `${rect.bottom}px`, width: `${rect.width}px` };
					});
				}
			}, 400);
		},

		async selectSuggestion(suggestion) {
			if (this.inUpdateMode || this.scope === 'organisation') {
				return;
			}

			const classroom = this.classroomsStore.currentClassroom;

			this.editableModel = suggestion.clone();
			await this.editableModel.toggleClassroom(classroom, true);

			// Clear autocomplete suggestions and reset focus state after selecting a pupil
			this.autoCompleteSuggestions = [];
			this.focusedInput = null;
			this.focusedField = null;
		},
	},
}
</script>
